import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";

// Cross-portal analytics hook.
// - Auto-tracks page views on route change (one event per location change)
// - Exposes trackEvent(featureName, action, metadata) for explicit instrumentation
// - Batches client-side: events are queued and flushed every ~5s or every 10
//   events, whichever comes first. Uses navigator.sendBeacon on unload to
//   minimize data loss without delaying tab close.
// - Never blocks UX: failures swallowed silently.

interface QueuedEvent {
  feature_name: string;
  feature_category?: string;
  page_path?: string;
  action?: string;
  session_id?: string;
  metadata?: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 10;
const ENDPOINT = "/api/analytics/events";

// One queue per page-load. Outside the hook so multiple mounts share it.
const queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function sessionId(): string {
  try {
    let s = sessionStorage.getItem("analytics:session");
    if (!s) {
      s = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("analytics:session", s);
    }
    return s;
  } catch {
    return "anon-session";
  }
}

function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  // Each event is its own POST. The endpoint is cheap and serial-firing avoids
  // schema/batching complexity. If volume justifies it later, swap to a batch
  // endpoint without changing the call site.
  for (const ev of batch) {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(ev),
      keepalive: true, // allow firing during page unload
    }).catch(() => {});
  }
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function enqueue(event: QueuedEvent) {
  queue.push({ ...event, session_id: event.session_id ?? sessionId() });
  if (queue.length >= FLUSH_THRESHOLD) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

// Flush on page hide via sendBeacon for best-effort delivery
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    try {
      if (navigator.sendBeacon) {
        for (const ev of batch) {
          const blob = new Blob([JSON.stringify(ev)], { type: "application/json" });
          navigator.sendBeacon(ENDPOINT, blob);
        }
      }
    } catch {}
  });
}

export function useAnalytics() {
  const [location] = useLocation();
  const lastPathRef = useRef<string | null>(null);

  // Auto-track page views — one event per actual change
  useEffect(() => {
    if (lastPathRef.current === location) return;
    lastPathRef.current = location;
    enqueue({
      feature_name: "page_view",
      action: "page_view",
      page_path: location,
    });
  }, [location]);

  const trackEvent = useCallback(
    (featureName: string, action: string = "click", metadata?: Record<string, unknown>) => {
      enqueue({
        feature_name: featureName,
        action,
        page_path: window.location.pathname,
        metadata,
      });
    },
    []
  );

  return { trackEvent };
}
